import React, { FC, PropsWithChildren, useCallback, useMemo, useRef, useState } from "react";
import { View } from "react-native";
import { Country } from "@lookiero/sty-psp-locale";
import { AnswersProjection, AnswersProvider, QuestionProjection } from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { Segment } from "@lookiero/sty-psp-segment";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Answers } from "../../../../../../domain/screen/model/screen";
import { NextBestActionType } from "../../../../../../projection/nextBestActionDefinition/nextBestActionDefinition";
import type { NextBestActionDefinitionAction } from "../../../../../../projection/nextBestActionDefinition/nextBestActionDefinition";
import { useTrackNextBestActionClick } from "../../../../../tracking/useTrackNextBestActionClick";
import {
  NextBestActionCardOnActionTriggeredFunction,
  NextBestActionCardStatus,
} from "../../../../components/nextBestActionItem/NextBestActionItem";
import { ScreenModal } from "../../../screen/ScreenModal";
import { NextBestAction } from "../../NextBestAction";
import { style as nextBestActionStyle } from "../../NextBestAction.style";
import { NextBestActionProvider } from "../../behaviours/useNextBestAction";
import { NextBestActionItemProvider, NextBestActionItems } from "../../behaviours/useNextBestActionItem";
import { NextBestActionCard } from "../nextBestActionCard/NextBestActionCard";
import { NextBestActionCta } from "../nextBestActionCta/NextBestActionCta";
import { NextBestActionHostDefault } from "../nextBestActionHostDefault/NextBestActionHostDefault";
import { NextBestActionHostText } from "../nextBestActionHostText/NextBestActionHostText";
import { NextBestActionHostWeightImperial } from "../nextBestActionHostWeightImperial/NextBestActionHostWeightImperial";
import { NextBestActionHostWeightMetric } from "../nextBestActionHostWeightMetric/NextBestActionHostWeightMetric";
import { findCardActionBySlug } from "./nextBestActionForm.utils";

interface NextBestActionFormProps {
  readonly actions: NextBestActionDefinitionAction[];
  readonly answers: AnswersProjection;
  readonly basePath: string;
  readonly country: Country;
  readonly screenModalProviders: FC<PropsWithChildren>;
  readonly segment: Segment;
  readonly tradename: Tradename;
  readonly customerId: string;
  readonly onPressCta: () => void;
}

interface HostScreenOnSuccessFunctionArgs {
  readonly answers: Answers;
}

interface HostScreenOnSuccessFunction {
  (args: HostScreenOnSuccessFunctionArgs): void;
}

const NullNextBestActionItem: FC = () => null;

const baseNextBestActionItems: NextBestActionItems = {
  [NextBestActionType.HOST_DEFAULT]: NextBestActionHostDefault,
  [NextBestActionType.CARD]: NextBestActionCard,
  [NextBestActionType.CTA]: NextBestActionCta,
  [NextBestActionType.TEXT]: NullNextBestActionItem,
  [NextBestActionType.HOST_WEIGHT_METRIC]: NextBestActionHostWeightMetric,
  [NextBestActionType.HOST_WEIGHT_IMPERIAL]: NextBestActionHostWeightImperial,
  [NextBestActionType.HOST_TEXT]: NextBestActionHostText,
  [NextBestActionType.NUMBER_WITH_CURRENCY]: NullNextBestActionItem,
};

const NextBestActionForm: FC<NextBestActionFormProps> = ({
  actions,
  answers,
  basePath,
  country,
  screenModalProviders,
  segment,
  tradename,
  customerId,
  onPressCta,
}) => {
  const style = useMemo(() => nextBestActionStyle(), []);

  const [actionStatuses, setActionStatuses] = useState<Record<string, NextBestActionCardStatus>>({});
  const [screenSlug, setScreenSlug] = useState<string>();
  const screenSlugRef = useRef(screenSlug);
  screenSlugRef.current = screenSlug || screenSlugRef.current;

  const trackNextBestActionClick = useTrackNextBestActionClick({ country, customerId, segment, tradename });
  const closeScreen = useCallback(() => setScreenSlug(""), []);

  const handleOnActionTriggered: NextBestActionCardOnActionTriggeredFunction = useCallback(
    ({ action }) => {
      setActionStatuses((prev) => ({ ...prev, [action.id]: NextBestActionCardStatus.IDLE }));

      if (action.type === NextBestActionType.CARD) {
        const cardAction = action as NextBestActionDefinitionAction<NextBestActionType.CARD>;
        const metadata = cardAction.metadata;

        if (metadata && metadata.slug) {
          trackNextBestActionClick({ slug: metadata.slug });
          setScreenSlug(metadata.slug);
        }
      }
      if (action.type === NextBestActionType.CTA) {
        trackNextBestActionClick({});
      }
    },
    [trackNextBestActionClick],
  );

  const handleScreenFilledSuccess: HostScreenOnSuccessFunction = useCallback(
    ({ answers }) => {
      closeScreen();

      const aswersAreEmpty = Object.values(answers).every((answer: string[]) => answer.length === 0);

      if (aswersAreEmpty) {
        return;
      }

      const currentSlug = screenSlugRef.current;
      if (!currentSlug) {
        return;
      }

      const cardAction = findCardActionBySlug(actions, currentSlug);

      if (!cardAction) {
        return;
      }

      setActionStatuses((prev) => ({ ...prev, [cardAction.id]: NextBestActionCardStatus.SUCCESS }));
    },
    [actions, closeScreen],
  );

  const handleOnPressCta = useCallback(() => {
    trackNextBestActionClick({});
    onPressCta();
  }, [onPressCta, trackNextBestActionClick]);

  const contextValue = useMemo(
    () => ({
      actionStatuses,
      onActionTriggered: handleOnActionTriggered,
      onPressCta: handleOnPressCta,
    }),
    [actionStatuses, handleOnActionTriggered, handleOnPressCta],
  );

  const nextBestActionItems = useMemo<NextBestActionItems>(() => ({ ...baseNextBestActionItems }), []);

  return (
    <View style={style.container}>
      <NextBestActionProvider value={contextValue}>
        <NextBestActionItemProvider nextBestActionItems={nextBestActionItems}>
          <AnswersProvider answers={answers} questions={actions as unknown as QuestionProjection[]}>
            {actions.map((action) => (
              <NextBestAction key={action.id} action={action} basePath={basePath} country={country} segment={segment} />
            ))}
          </AnswersProvider>
        </NextBestActionItemProvider>
      </NextBestActionProvider>

      <ScreenModal
        providers={screenModalProviders}
        slug={screenSlug}
        onBack={closeScreen}
        onSuccess={handleScreenFilledSuccess}
      />
    </View>
  );
};

export { NextBestActionForm };
